/**
 * Group dance overlay (รำกลุ่ม): the rhythm track for the ring under way. Five portraits with
 * whose turn it is, the arrows sliding to the hit ring in pixel art, perfect/good/miss as they are
 * judged, the harmony meter, the round counter and, at the end, what the dance paid. It only reads
 * `world.dance` and sends commands: the arrow keys (or the touch pad) send `danceInput` stamped with
 * the world time of the press, which is what the rules judge (packages/shared groupDance.ts).
 *
 * While the player stands in the ring the arrow keys belong to the dance: they are caught before
 * the movement keys see them, so tapping an arrow never walks the player out of the ring.
 */
import {
  DANCE_END_TEXT,
  DANCE_ROUNDS,
  GROUP_DANCE,
  danceGradeOf,
  type DanceDir,
  type DanceGrade,
  type DanceReward,
  type PlayerCommand,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { drawPortrait, type SpriteBank } from '../render/sprites';
import { $, h, setText, setWidth } from './dom';

const KEYS: Readonly<Record<string, DanceDir>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};
const GRADE_TEXT: Readonly<Record<DanceGrade, string>> = { perfect: 'เยี่ยม!', good: 'ดี', miss: 'พลาด' };
const DIR_TEXT: Readonly<Record<DanceDir, string>> = { up: '▲', down: '▼', left: '◀', right: '▶' };

// ───────────────────────── pixel art ─────────────────────────

/** The up arrow's shape on an 11×11 grid; the others are it turned. */
const ARROW = [
  '.....#.....',
  '....###....',
  '...#####...',
  '..#######..',
  '.#########.',
  '###########',
  '....###....',
  '....###....',
  '....###....',
  '....###....',
  '...........',
];
/** Natural dyes: jade, indigo, cinnabar, ochre — each [light, body, shade]. */
const DYES: Readonly<Record<DanceDir, readonly [string, string, string]>> = {
  up: ['#b6dcb4', '#5f9e7f', '#2f5a4a'],
  down: ['#a9bedc', '#4f6e9e', '#273a5c'],
  left: ['#eaa98e', '#b5523f', '#6a2a24'],
  right: ['#f2d79a', '#c99a45', '#6e4e22'],
};
const OUTLINE = '#120e14';

function filled(dir: DanceDir, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x > 10 || y > 10) return false;
  // Up as drawn; down mirrored; left transposed; right transposed and mirrored.
  const [u, v] = dir === 'up' ? [x, y] : dir === 'down' ? [x, 10 - y] : dir === 'left' ? [y, x] : [y, 10 - x];
  return ARROW[v]![u] === '#';
}

/**
 * An arrow as a little lit volume: one warm key light from the upper right (its edges bright),
 * cool shade on the lower left, a dark outline round it all. `tone` dims it (0–1) for a miss.
 */
function arrowArt(dir: DanceDir, tone: 'lit' | 'grey'): HTMLCanvasElement {
  const c = h('canvas');
  c.width = 13;
  c.height = 13;
  const g = c.getContext('2d')!;
  const [light, body, shade] = tone === 'grey' ? (['#8a8290', '#5c5562', '#36303c'] as const) : DYES[dir];
  for (let y = -1; y <= 11; y++)
    for (let x = -1; x <= 11; x++) {
      const on = filled(dir, x, y);
      let col: string | null = null;
      if (on) {
        const lit = !filled(dir, x + 1, y) || !filled(dir, x, y - 1);
        const dark = !filled(dir, x - 1, y) || !filled(dir, x, y + 1);
        col = lit && !dark ? light : dark && !lit ? shade : body;
      } else if (
        filled(dir, x + 1, y) ||
        filled(dir, x - 1, y) ||
        filled(dir, x, y + 1) ||
        filled(dir, x, y - 1)
      )
        col = OUTLINE;
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(x + 1, y + 1, 1, 1);
    }
  return c;
}

const ART: Record<string, HTMLCanvasElement> = {};
const arrowOf = (dir: DanceDir, tone: 'lit' | 'grey') => (ART[`${dir}${tone}`] ??= arrowArt(dir, tone));

/** Track size in art pixels (drawn three times larger), the hit ring's x and the scroll speed. */
const TW = 168;
const TH = 23;
const HIT_X = 18;
const SPEED = 62;

// ───────────────────────── the overlay ─────────────────────────

export class DanceOverlay {
  private readonly button = h('button', {
    type: 'button',
    id: 'danceBtn',
    title: 'รำกลุ่ม: ตั้งวงรำห้าคน สิบรอบ',
  });
  private readonly root = h('div', { id: 'danceOverlay', hidden: true });
  private readonly head = h('div', { class: 'dz-head' });
  private readonly round = h('b');
  private readonly status = h('span', { class: 'dz-status' });
  private readonly score = h('span', { class: 'dz-score' });
  private readonly ring = h('div', { class: 'dz-ring' });
  private readonly harmony = h('div', { class: 'dz-meter' }, h('i'), h('span'));
  private readonly track = h('canvas', { class: 'dz-track px' });
  private readonly g: CanvasRenderingContext2D;
  private readonly flash = h('div', { class: 'dz-flash' });
  private readonly pad = h('div', { class: 'dz-pad' });
  private readonly result = h('div', { id: 'danceResult', hidden: true });
  private portraits: { id: number; root: HTMLElement; c: HTMLCanvasElement; acc: HTMLElement }[] = [];
  private ringKey = '';
  /** The last judged arrow, for the burst on the ring. */
  private burst: { grade: DanceGrade; t: number } | null = null;
  private resultT = 0;

  constructor(
    private readonly world: World,
    private readonly sprites: SpriteBank,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.button.append(h('i', { class: 'gm-mark' }, '人'), h('span', {}, 'รำกลุ่ม'));
    this.button.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.send(this.world.dance.me() ? { t: 'danceLeave' } : { t: 'danceStart' });
    });
    this.track.width = TW;
    this.track.height = TH;
    this.g = this.track.getContext('2d')!;
    this.g.imageSmoothingEnabled = false;
    const leave = h('button', { type: 'button', class: 'btn small ghost' }, 'ออกจากวง');
    leave.addEventListener('click', () => this.send({ t: 'danceLeave' }));
    this.head.append(h('strong', {}, 'รำกลุ่ม'), this.round, this.status, this.score, leave);
    for (const dir of ['left', 'up', 'down', 'right'] as const) {
      const b = h('button', { type: 'button', class: `dz-key ${dir}`, title: DIR_TEXT[dir] });
      const art = arrowOf(dir, 'lit');
      const c = h('canvas', { class: 'px' });
      c.width = art.width;
      c.height = art.height;
      c.getContext('2d')!.drawImage(art, 0, 0);
      b.append(c);
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.press(dir);
      });
      this.pad.append(b);
    }
    this.root.append(
      this.head,
      this.ring,
      h('div', { class: 'dz-lane' }, this.track, this.flash),
      h('div', { class: 'dz-foot' }, h('small', {}, 'ความกลมเกลียว'), this.harmony),
      this.pad,
      h('small', { class: 'dz-hint' }, 'กดลูกศรตามจังหวะเมื่อถึงตาท่าน · เดินออกจากวงหรือถูกโจมตีจะหยุดรำ'),
    );
    for (const el of [this.root, this.result]) el.addEventListener('pointerdown', (e) => e.stopPropagation());
    // The button sits in the HUD's menu (เมนู Esc).
    $('#gmPlay').append(this.button);
    document.body.append(this.root, this.result);
    // Caught before the movement keys (Input listens on the bubble), only while in the ring.
    addEventListener('keydown', this.onKey, true);
    addEventListener('keyup', this.onKeyUp, true);
  }

  private onKey = (e: KeyboardEvent): void => {
    const dir = KEYS[e.key];
    if (!dir || !this.world.dance.me()) return;
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!e.repeat) this.press(dir);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (KEYS[e.key] && this.world.dance.me()) e.stopImmediatePropagation();
  };

  /** An arrow pressed: stamped with the world time now, judged by the rules. */
  private press(dir: DanceDir): void {
    this.send({ t: 'danceInput', dir, at: this.world.time });
  }

  handle(ev: WorldEvent): void {
    switch (ev.type) {
      case 'danceStart':
        this.ringKey = '';
        this.result.hidden = true;
        break;
      case 'danceArrow': {
        this.burst = { grade: ev.grade, t: this.world.time };
        const mine = ev.dancer === this.world.player.id;
        this.flash.className = `dz-flash ${ev.grade} ${mine ? 'mine' : ''}`;
        this.flash.textContent =
          ev.grade === 'miss'
            ? GRADE_TEXT.miss
            : `${GRADE_TEXT[ev.grade]}${ev.combo > 2 ? ` · คอมโบ ${ev.combo}` : ''}`;
        // Restart the pop animation.
        void this.flash.offsetWidth;
        this.flash.classList.add('pop');
        break;
      }
      case 'danceEnd':
        this.showResult(ev.reason, ev.reward);
        break;
    }
  }

  private showResult(reason: keyof typeof DANCE_END_TEXT, r: DanceReward | null): void {
    const ok = h('button', { type: 'button', class: 'btn small' }, 'ตกลง');
    ok.addEventListener('click', () => (this.result.hidden = true));
    const s = this.world.dance.session;
    const lines: (HTMLElement | string)[] = [];
    if (r) {
      lines.push(
        h('div', { class: `dz-grade g${r.grade}` }, r.grade),
        h('p', { class: 'dz-rank' }, danceGradeOf(r.perf).name),
        h(
          'ul',
          {},
          h('li', {}, `รอบที่รำ ${r.rounds}/${GROUP_DANCE.rounds}`),
          h('li', {}, `แต้มฝึกปรือ +${r.cultivation.toLocaleString('th-TH')}`),
          h(
            'li',
            {},
            `พลังประสาน ฝึกปรือและฝึกปราณเร็วขึ้น ×${r.mult} นาน ${Math.round(r.seconds / 60)} นาที`,
          ),
          h('li', {}, `รับรางวัลได้อีก ${r.runsLeft} ครั้งวันนี้`),
        ),
      );
      if (s)
        lines.push(h('small', {}, `คอมโบสูงสุด ${s.maxCombo} · คะแนน ${s.score.toLocaleString('th-TH')}`));
    } else
      lines.push(
        h(
          'p',
          {},
          s && !s.rewarded
            ? `วันนี้รับรางวัลครบ ${GROUP_DANCE.dailyRuns} ครั้งแล้ว`
            : `รำไม่ถึง ${GROUP_DANCE.partialMinRounds} รอบ จึงไม่ได้รับรางวัล`,
        ),
      );
    this.result.replaceChildren(h('header', {}, h('b', {}, DANCE_END_TEXT[reason])), ...lines, ok);
    this.result.hidden = false;
    this.resultT = r ? 15 : 5;
  }

  /** Per frame: the button, the ring and the track. */
  sync(dt: number): void {
    if (!this.result.hidden && (this.resultT -= dt) <= 0) this.result.hidden = true;
    const d = this.world.dance;
    const s = d.session;
    const me = d.me();
    setText(this.button.querySelector('span')!, me ? 'ออกจากวง' : 'รำกลุ่ม');
    this.button.classList.toggle('on', !!me);
    this.root.hidden = !s || !me || s.phase === 'finale';
    if (this.root.hidden || !s) return;
    const now = this.world.time;
    const t = s.turn;

    // Header: round, whose turn, score.
    const r = Math.min(s.round + 1, GROUP_DANCE.rounds);
    setText(this.round, `รอบ ${r}/${GROUP_DANCE.rounds}`);
    const who = t ? s.dancers[t.dancer]! : null;
    setText(
      this.status,
      s.phase === 'gather'
        ? `วงรำกำลังตั้ง… ${Math.max(0, Math.ceil(s.phaseEnd - now))}`
        : s.phase === 'pause'
          ? `จบรอบ ${s.roundsDone} · รอบต่อไป ${DANCE_ROUNDS[s.round + 1]?.arrows ?? 0} ลูกศร`
          : who === me
            ? 'ตาท่าน! กดลูกศรตามจังหวะ'
            : `ตาของ${who?.name ?? ''}`,
    );
    this.status.classList.toggle('mine', who === me && s.phase === 'turn');
    setText(this.score, `คะแนน ${s.score.toLocaleString('th-TH')}`);

    // The five portraits: whose turn it is lit, the gone greyed.
    const key = s.dancers.map((x) => x.id).join();
    if (key !== this.ringKey) this.buildRing(key);
    this.portraits.forEach((p, i) => {
      const x = s.dancers[i]!;
      p.root.classList.toggle('on', s.phase === 'turn' && t?.dancer === i);
      p.root.classList.toggle('out', x.out);
      setText(p.acc, x.arrows ? `${Math.round((x.acc / x.arrows) * 100)}%` : '—');
    });

    setWidth(this.harmony.querySelector('i')!, s.harmony / 100);
    setText(this.harmony.querySelector('span')!, `${Math.round(s.harmony)} · คอมโบ ${s.combo}`);
    this.harmony.classList.toggle('high', s.harmony >= 75);
    this.harmony.classList.toggle('low', s.harmony < 30);
    this.pad.classList.toggle('mine', who === me && s.phase === 'turn');
    this.drawTrack(now, who === me);
  }

  private buildRing(key: string): void {
    this.ringKey = key;
    const s = this.world.dance.session!;
    this.portraits = s.dancers.map((x) => {
      const c = h('canvas', { class: 'px' });
      c.width = 26;
      c.height = 26;
      const acc = h('em');
      const root = h('div', { class: 'dz-p' }, c, h('span', {}, x.name), acc);
      return { id: x.id, root, c, acc };
    });
    this.ring.replaceChildren(...this.portraits.map((p) => p.root));
    // Portraits come from the figures, which may still be drawing: try again shortly.
    const paint = () => {
      for (const p of this.portraits) {
        const e = this.world.get(p.id);
        drawPortrait(p.c, (e && this.sprites.of(e)) ?? this.sprites.base.hero!);
      }
    };
    paint();
    for (const ms of [300, 1200]) setTimeout(() => this.ringKey === key && paint(), ms);
  }

  /** The lane: lacquered wood, the bronze hit ring, the arrows sliding in, the last judgement's burst. */
  private drawTrack(now: number, mine: boolean): void {
    const g = this.g;
    const s = this.world.dance.session!;
    g.clearRect(0, 0, TW, TH);
    // Lane: dark lacquer lit along its top edge, shaded along its bottom.
    g.fillStyle = '#2a1e1c';
    g.fillRect(0, 1, TW, TH - 2);
    g.fillStyle = '#4a3428';
    g.fillRect(0, 1, TW, 1);
    g.fillStyle = '#171012';
    g.fillRect(0, TH - 2, TW, 1);
    g.fillStyle = '#3a2a24';
    for (let x = 6; x < TW; x += 24) g.fillRect(x, TH / 2, 1, 1);
    // Hit ring: bronze, bright upper right; it glows while an arrow is in the window.
    const t = s.turn;
    const next = t?.arrows.find((a) => a.grade === null);
    const near = next && Math.abs(next.at - now) <= GROUP_DANCE.good;
    const cy = Math.floor(TH / 2);
    const ring = (r: number, col: string) => {
      g.fillStyle = col;
      for (let a = 0; a < 40; a++) {
        const th = (a / 40) * Math.PI * 2;
        g.fillRect(Math.round(HIT_X + Math.cos(th) * r), Math.round(cy + Math.sin(th) * r), 1, 1);
      }
    };
    ring(8, near ? (mine ? '#f6dc8c' : '#b8a070') : '#8a6a3a');
    ring(9, '#3a2614');
    g.fillStyle = near ? '#fff2c0' : '#d9b46c';
    g.fillRect(HIT_X + 4, cy - 7, 2, 1);
    g.fillRect(HIT_X + 6, cy - 6, 1, 2);
    // Arrows still to come (and misses sliding on, greyed).
    if (t)
      for (const a of t.arrows) {
        const x = Math.round(HIT_X + (a.at - now) * SPEED);
        if (x < -8 || x > TW + 8) continue;
        if (a.grade === 'perfect' || a.grade === 'good') continue;
        const art = arrowOf(a.dir, a.grade === 'miss' ? 'grey' : 'lit');
        g.globalAlpha = mine || a.grade ? 1 : 0.75;
        g.drawImage(art, x - 6, cy - 6);
        g.globalAlpha = 1;
      }
    // The burst on a hit: gold for perfect, jade for good, a cinnabar cross for a miss.
    const b = this.burst;
    const age = b ? now - b.t : 1;
    if (b && age < 0.3) {
      const r = 9 + Math.floor(age * 30);
      if (b.grade === 'miss') {
        g.fillStyle = '#c8443b';
        for (let k = -3; k <= 3; k++) {
          g.fillRect(HIT_X + k, cy + k, 1, 1);
          g.fillRect(HIT_X + k, cy - k, 1, 1);
        }
      } else ring(r, b.grade === 'perfect' ? '#ffe7a0' : '#8fe0b8');
    }
  }
}
